import { Helmet } from "react-helmet-async";
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import api from "../Utils/Axios";

function AdminExpenseManagement() {
  const navigate = useNavigate();
  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [expenses, setExpenses] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [filterSearch, setFilterSearch] = useState('');
  const [filterDateFrom, setFilterDateFrom] = useState('');
  const [filterDateTo, setFilterDateTo] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');

  const getToken = () => localStorage.getItem("adminToken");

  useEffect(() => {
    const token = getToken();
    if (!token) {
      navigate("/admin/login");
      return;
    }
    fetchCompanies();
  }, [navigate]);

  const fetchCompanies = async () => {
    try {
      const token = getToken();
      const response = await api.get("/admin/companies", {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCompanies(response.data.companies || []);
    } catch (error) {
      console.error("Fetch companies error:", error);
      toast.error("Failed to load companies");
    }
  };

  const fetchExpenses = async (companyId) => {
    if (!companyId) {
      setExpenses([]);
      return;
    }
    
    setIsLoading(true);
    try {
      // For admin, we need to fetch expenses for a specific company
      // Since the API uses company token, we'll need to create an admin endpoint
      // For now, we'll use a workaround - admin can view but not directly manage
      // This would require backend changes to add admin routes for expenses
      
      // Note: This requires backend admin routes for expense management
      // For now showing message that admin routes need to be added
      toast.info("Admin expense management requires backend routes. Please contact developer.");
      setExpenses([]);
    } catch (error) {
      console.error("Fetch expenses error:", error);
      toast.error("Failed to load expenses");
      setExpenses([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCompanySelect = (companyId) => {
    const company = companies.find(c => c._id === companyId);
    setSelectedCompany(company);
    if (companyId) {
      fetchExpenses(companyId);
    } else {
      setExpenses([]);
    }
  };

  const getUniqueCategories = () => {
    return [...new Set(expenses.map(exp => exp.category).filter(Boolean))];
  };

  const filteredExpenses = expenses.filter(exp => {
    const matchesSearch = !filterSearch || 
      (exp.description && exp.description.toLowerCase().includes(filterSearch.toLowerCase())) ||
      (exp.expenseNumber && exp.expenseNumber.toLowerCase().includes(filterSearch.toLowerCase())) ||
      (exp.category && exp.category.toLowerCase().includes(filterSearch.toLowerCase()));
    
    const matchesDateFrom = !filterDateFrom || exp.date >= filterDateFrom;
    const matchesDateTo = !filterDateTo || exp.date <= filterDateTo;
    const matchesCategory = filterCategory === 'all' || exp.category === filterCategory;
    const matchesStatus = filterStatus === 'all' || exp.status === filterStatus;
    
    return matchesSearch && matchesDateFrom && matchesDateTo && matchesCategory && matchesStatus;
  });

  const handleDelete = async (expenseId) => {
    if (!window.confirm("Are you sure you want to delete this expense?")) {
      return;
    }
    
    try {
      setIsLoading(true);
      const token = getToken();
      // This would require admin delete endpoint
      toast.info("Delete functionality requires admin backend routes");
    } catch (error) {
      console.error("Delete expense error:", error);
      toast.error("Failed to delete expense");
    } finally {
      setIsLoading(false);
    }
  };

  // Calculate category totals
  const categoryTotals = {};
  filteredExpenses.forEach(exp => {
    const category = exp.category || 'Uncategorized';
    if (!categoryTotals[category]) {
      categoryTotals[category] = 0;
    }
    categoryTotals[category] += parseFloat(exp.amount || 0);
  });

  return (
    <>
      <Helmet><title>Expense Management - Admin Panel</title></Helmet>
      <div className="min-h-screen bg-brand-background p-4 md:p-8">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="bg-white rounded-lg shadow-md p-6 mb-6 border border-brand-card/60 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-brand-header">Expense Management</h1>
              <p className="text-gray-600 mt-1 text-sm md:text-base">
                View and manage expenses for all companies.
              </p>
            </div>
            <div className="flex flex-col md:flex-row gap-3">
              <button
                onClick={() => navigate("/admin")}
                className="bg-white border border-brand-header text-brand-header hover:bg-brand-header hover:text-brand-background font-semibold px-4 py-2 rounded-lg transition text-sm"
              >
                Back to Admin
              </button>
            </div>
          </div>

          {/* Company Selector */}
          <div className="bg-white rounded-lg shadow-md p-6 mb-6 border border-brand-card/40">
            <h2 className="text-lg font-semibold text-gray-900 mb-3">Select Company</h2>
            <select
              value={selectedCompany?._id || ""}
              onChange={(e) => handleCompanySelect(e.target.value)}
              className="w-full max-w-md px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-button"
            >
              <option value="">-- Select Company --</option>
              {companies.map((company) => (
                <option key={company._id} value={company._id}>
                  {company.shopName || company.username} ({company.email})
                </option>
              ))}
            </select>
            {selectedCompany && (
              <p className="mt-2 text-sm text-gray-600">
                Viewing expenses for: <span className="font-semibold">{selectedCompany.shopName || selectedCompany.username}</span>
              </p>
            )}
          </div>

          {selectedCompany && (
            <>
              {/* Filters */}
              <div className="bg-white rounded-lg shadow-md p-6 mb-6 border border-brand-card/40">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Filters</h3>
                <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Search</label>
                    <input
                      type="text"
                      value={filterSearch}
                      onChange={(e) => setFilterSearch(e.target.value)}
                      placeholder="Description, number, or category"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-button"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Date From</label>
                    <input
                      type="date"
                      value={filterDateFrom}
                      onChange={(e) => setFilterDateFrom(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-button"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Date To</label>
                    <input
                      type="date"
                      value={filterDateTo}
                      onChange={(e) => setFilterDateTo(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-button"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Category</label>
                    <select
                      value={filterCategory}
                      onChange={(e) => setFilterCategory(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-button"
                    >
                      <option value="all">All</option>
                      {getUniqueCategories().map(cat => (
                        <option key={cat} value={cat}>{cat}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
                    <select
                      value={filterStatus}
                      onChange={(e) => setFilterStatus(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-button"
                    >
                      <option value="all">All</option>
                      <option value="completed">Completed</option>
                      <option value="pending">Pending</option>
                    </select>
                  </div>
                </div>
                <button
                  onClick={() => {
                    setFilterSearch('');
                    setFilterDateFrom('');
                    setFilterDateTo('');
                    setFilterCategory('all');
                    setFilterStatus('all');
                  }}
                  className="mt-4 text-sm text-brand-header hover:text-emerald-800 font-medium"
                >
                  Clear Filters
                </button>
              </div>

              {/* Category Summary */}
              {Object.keys(categoryTotals).length > 0 && (
                <div className="bg-gradient-to-br from-orange-50 to-red-50 rounded-lg shadow-md p-6 mb-6 border-2 border-orange-200">
                  <h3 className="text-xl font-bold mb-4 text-orange-800">Expense Summary by Category</h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                    {Object.entries(categoryTotals).map(([category, total]) => (
                      <div key={category} className="bg-white rounded-lg p-4 border-2 border-orange-200 shadow-md">
                        <div className="text-sm font-semibold text-gray-700 mb-2">{category}</div>
                        <div className="text-lg font-bold text-orange-600">{total.toFixed(2)}</div>
                      </div>
                    ))}
                    <div className="bg-gradient-to-br from-orange-600 to-red-600 rounded-lg p-4 border-2 border-orange-400 shadow-lg">
                      <div className="text-sm font-semibold text-white mb-2">Total Expenses</div>
                      <div className="text-xl font-bold text-white">
                        {Object.values(categoryTotals).reduce((sum, val) => sum + val, 0).toFixed(2)}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Expenses Table */}
              <div className="bg-white rounded-lg shadow-md overflow-hidden border border-brand-card/40">
                <div className="p-6 border-b border-gray-200">
                  <h3 className="text-lg font-semibold text-gray-900">
                    Expenses ({filteredExpenses.length} / {expenses.length})
                  </h3>
                </div>
                {isLoading ? (
                  <div className="p-8 text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-t-4 border-b-4 border-brand-header mx-auto"></div>
                  </div>
                ) : filteredExpenses.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">Date</th>
                          <th className="px-4 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">Expense Number</th>
                          <th className="px-4 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">Category</th>
                          <th className="px-4 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">Description</th>
                          <th className="px-4 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">Amount</th>
                          <th className="px-4 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">Payment Method</th>
                          <th className="px-4 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">Status</th>
                          <th className="px-4 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {filteredExpenses.map((exp) => (
                          <tr key={exp.id} className="hover:bg-gray-50">
                            <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">{exp.date}</td>
                            <td className="px-4 py-3 whitespace-nowrap text-sm font-semibold text-orange-600">{exp.expenseNumber}</td>
                            <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">{exp.category || '-'}</td>
                            <td className="px-4 py-3 text-sm text-gray-700">{exp.description || '-'}</td>
                            <td className="px-4 py-3 whitespace-nowrap text-sm font-bold text-orange-600">{parseFloat(exp.amount || 0).toFixed(2)}</td>
                            <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">{exp.paymentMethod || '-'}</td>
                            <td className="px-4 py-3 whitespace-nowrap">
                              <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
                                exp.status === 'completed' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                              }`}>
                                {exp.status || 'completed'}
                              </span>
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap text-sm">
                              <button
                                onClick={() => handleDelete(exp.id)}
                                className="text-red-600 hover:text-red-800 font-medium"
                              >
                                Delete
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="p-8 text-center text-gray-500">
                    <p>No expenses found{filteredExpenses.length === 0 && expenses.length > 0 ? ' matching filters' : ''}</p>
                  </div>
                )}
              </div>
            </>
          )}

          {!selectedCompany && (
            <div className="bg-white rounded-lg shadow-md p-8 text-center border border-brand-card/40">
              <p className="text-gray-500">Please select a company to view expenses</p>
            </div>
          )}
        </div>
      </div>
    </>
  );
}

export default AdminExpenseManagement;

